import { createContext, useContext, Dispatch, SetStateAction, useState, useMemo, useEffect } from 'react';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { composeTabs, MediaIdType, MediaType, PostType } from '@sendible/shared-state-bridge';
import { EVENTS, subscribe, publish } from '@sendible/event-manager';
import { helpers, useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import { composeMediaInteractions } from '@sendible/frontendv2/src/components/MediaSystem/composeMediaInteractions';
import { MAX_MEDIA_LIMIT, COMPOSE_TABS, BULK_IMPORT_MAX_MEDIA } from './consts';
import { MediaFileType } from '../../models/medias/types';
import { useBackgroundUploaderContext } from '../BackgroundUploader/context';
import { useUploadsContext } from '../../models/uploads/uploadsContext';
import { singleFileUploadSuccess } from '../../pages/MediaLibrary/pendoEvents';
import { useGetMediasById } from '../../models/medias/index';

export type ComposeBoxContextType = {
  activePostType: PostType;
  isComposeBoxOpen: boolean;
  isCanvaModalOpen: boolean;
  isFlickrModalOpen: boolean;
  isGiphyModalOpen: boolean;
  isMediaAttacherOpen: boolean;
  isPexelsModalOpen: boolean;
  isRecentlyUsedModalOpen: boolean;
  isURLModalOpen: boolean;
  isYoutubeModalOpen: boolean;
  urlModalInitialUrl: string;
  isGoogleDriveModalOpen: boolean;
  activeComposeBoxTab: composeTabs;
  profileListServices: composeTabs[];
  activeNetworkTabs: composeTabs[];
  setIsCanvaModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsFlickrModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsGiphyModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsPexelsModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsRecentlyUsedModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsURLModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsYoutubeModalOpen: Dispatch<SetStateAction<boolean>>;
  setUrlModalInitialUrl: Dispatch<SetStateAction<string>>;
  setIsGoogleDriveModalOpen: Dispatch<SetStateAction<boolean>>;
  uploadFromDevice: (files: File[]) => Promise<void>;
  uploadUrlsToCompose: (files: UrlUploadFileType[]) => void;
  attachExistingMedia: (items: { id: MediaIdType; type: MediaFileType }[]) => void;
  getRemainingMediaLimit: () => number;
};

export const ComposeBoxContext = createContext({} as ComposeBoxContextType);

export const ComposeBoxProvider = ({ children }: Component) => {
  const [isCanvaModalOpen, setIsCanvaModalOpen] = useState(false);
  const [isFlickrModalOpen, setIsFlickrModalOpen] = useState(false);
  const [isGiphyModalOpen, setIsGiphyModalOpen] = useState(false);
  const [isPexelsModalOpen, setIsPexelsModalOpen] = useState(false);
  const [isRecentlyUsedModalOpen, setIsRecentlyUsedModalOpen] = useState(false);
  const [isURLModalOpen, setIsURLModalOpen] = useState(false);
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
  const [isGoogleDriveModalOpen, setIsGoogleDriveModalOpen] = useState(false);
  const [urlModalInitialUrl, setUrlModalInitialUrl] = useState('');
  const [
    {
      compose: { tabsState, activeComposeBoxTab, isComposeBoxOpen, isMediaAttacherOpen, attachedTwMedia },
    },
  ] = useBridgeContext();
  const backgroundUploader = useBackgroundUploaderContext();
  const { uploadFiles, uploadUrls } = useUploadsContext();
  const { showToastNotification } = useToastNotificationContext();
  const { t } = useTranslation('compose_box');
  const activePostType = useMemo(() => tabsState[activeComposeBoxTab]?.postType || 'post', [tabsState, activeComposeBoxTab]);
  const profileListServices = tabsState[COMPOSE_TABS.GROUP]?.profileListServices || [];
  // NOTE: the tabs added directly for individually-selected profiles (as opposed to
  // profiles selected via a profile list, which are only reflected in profileListServices
  // above) - excludes the message/group/bulk-import pseudo tabs.
  const activeNetworkTabs = (Object.keys(tabsState) as composeTabs[]).filter(
    (tab) => tab !== COMPOSE_TABS.MESSAGE && tab !== COMPOSE_TABS.GROUP && tab !== COMPOSE_TABS.BULK_IMPORT
  );
  // Keep one stable session id per open lifecycle, and reset when compose closes.
  const sessionId = useMemo(() => (isComposeBoxOpen ? helpers.generateId() : null), [isComposeBoxOpen]);

  const attachedMediaIds = useMemo(() => Object.keys(attachedTwMedia || {}).map(Number), [attachedTwMedia]);
  const { data: attachedMediaData } = useGetMediasById(attachedMediaIds, sessionId || undefined);

  useEffect(() => {
    attachedMediaData?.forEach((media) => {
      if (media.status === 'Ready') {
        composeMediaInteractions.updateMediaProperties(media);
      }
    });
  }, [attachedMediaData]);

  // The bulk importer holds exactly one media item per row, and attaching to a row replaces
  // whatever it already had - so there the "count so far" is always 0 and a batch is cut down
  // to its first item, rather than racing several attaches into the same row.
  const isBulkImport = activeComposeBoxTab === COMPOSE_TABS.BULK_IMPORT;
  const getMediaCount = () => (isBulkImport ? 0 : composeMediaInteractions.getAttachedMediaCount(activeComposeBoxTab));
  const limitToTabCapacity = <T,>(items: T[]) => {
    if (!isBulkImport || items.length <= BULK_IMPORT_MAX_MEDIA) return items;

    showToastNotification({
      notificationText: t('max_1_media_per_row') || '',
      type: NotificationTypeEnum.Warning,
    });

    return items.slice(0, BULK_IMPORT_MAX_MEDIA);
  };

  const getRemainingMediaLimit = () => (isBulkImport ? BULK_IMPORT_MAX_MEDIA : MAX_MEDIA_LIMIT - getMediaCount());

  const uploadFromDevice = async (droppedFiles: File[]) => {
    const newFiles = limitToTabCapacity(droppedFiles);
    const mediaCount = getMediaCount();

    if (mediaCount + newFiles.length > MAX_MEDIA_LIMIT) {
      showToastNotification({
        notificationText: t('max_40_media') || '',
        type: NotificationTypeEnum.Error,
      });

      return;
    }

    publish(EVENTS.PRE_UPLOAD, null);

    const orderedFiles = newFiles.sort((firstFile, secondFile) => firstFile.name.localeCompare(secondFile.name));

    uploadFiles({
      files: orderedFiles,
      onUploadStarted: (file: File) => {
        let type: MediaFileType | undefined;

        if (file.type.includes('image')) {
          type = MediaFileType.Image;
        } else if (file.type.includes('video')) {
          type = MediaFileType.Video;
        } else if (file.type.includes('pdf')) {
          type = MediaFileType.Document;
        }

        publish(EVENTS.MEDIA_UPLOAD_STARTED, activeComposeBoxTab);

        return backgroundUploader.addUploadAndReturnId(file.name, type);
      },
      onMediaCreated: ({ file, fileId, mediaId, mediaType }: OnMediaCreatedFilesArgsType) => {
        backgroundUploader.updateFileMediaId({ fileId, mediaId });

        const orderNo = mediaCount + orderedFiles.indexOf(file) + 1;

        composeMediaInteractions.attachMedia({ id: mediaId, type: mediaType, orderNo }, activeComposeBoxTab);

        if (file) {
          window.pendo.track(singleFileUploadSuccess, {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          });
        }
      },
    });
  };

  const uploadUrlsToCompose = (urlFiles: UrlUploadFileType[]) => {
    const files = limitToTabCapacity(urlFiles);
    const mediaCount = getMediaCount();

    if (mediaCount + files.length > MAX_MEDIA_LIMIT) {
      showToastNotification({
        notificationText: t('max_40_media') || '',
        type: NotificationTypeEnum.Error,
      });

      return;
    }

    const orderedFiles = files.sort((firstFile, secondFile) => (firstFile.name || firstFile.url).localeCompare(secondFile.name || secondFile.url));

    uploadUrls({
      files: orderedFiles,
      onUploadStarted: () => {
        publish(EVENTS.MEDIA_UPLOAD_STARTED, activeComposeBoxTab);

        return backgroundUploader.addUploadAndReturnId(t('uploading_media'));
      },
      onMediaCreated: ({ fileId, mediaId, mediaType, url, name }: OnMediaCreatedUrlsArgsType) => {
        backgroundUploader.updateFileMediaId({ fileId, mediaId });
        backgroundUploader.updateFileName({ fileId, name });

        const orderNo = mediaCount + orderedFiles.findIndex((f) => f.url === url) + 1;

        composeMediaInteractions.attachMedia({ id: mediaId, type: mediaType, orderNo }, activeComposeBoxTab);
      },
    });
  };

  // Attaches media that already exists in the media system (e.g. picked from "Recently used")
  // - there is no upload, so on the bulk importer's tab MEDIA_UPLOAD_STARTED is published here
  // to keep its row attribution (see BulkImportView#attributeNewBulkImportMedia) working exactly
  // like it does for uploads: one start event immediately before each attach. Regular tabs
  // have no such listener, so nothing is announced for them.
  const attachExistingMedia = (items: { id: MediaIdType; type: MediaFileType }[]) => {
    // Re-picking media the bulk importer's row already holds must be a no-op: the media is
    // already attached (so no MEDIA_ADDED would ever attribute it) and announcing an upload
    // here would blank the row to an "Uploading" skeleton, leave a stale entry in the
    // importer's pending-row queue, and mis-attribute the next attach to this row.
    const alreadyAttachedIds = isBulkImport ? composeMediaInteractions.getAttachedMedia(activeComposeBoxTab) || {} : {};
    const toAttach = limitToTabCapacity(items.filter(({ id }) => !alreadyAttachedIds[id]));
    const mediaCount = getMediaCount();

    if (toAttach.length === 0) return;

    if (mediaCount + toAttach.length > MAX_MEDIA_LIMIT) {
      showToastNotification({
        notificationText: t('max_40_media') || '',
        type: NotificationTypeEnum.Error,
      });

      return;
    }

    toAttach.forEach(({ id, type }) => {
      if (isBulkImport) publish(EVENTS.MEDIA_UPLOAD_STARTED, activeComposeBoxTab);
      composeMediaInteractions.attachMedia({ id, type }, activeComposeBoxTab);
    });
  };

  // this is needed to load medias when media is uploaded
  useEffect(() => {
    const unsubscribeReady = subscribe(EVENTS.UPLOAD_READY, (media: MediaType) => {
      composeMediaInteractions.updateMediaProperties(media);
    });

    const unsubscribeFailed = subscribe(EVENTS.UPLOAD_FAILED, ({ mediaId }: FailedMediaType) => {
      if (mediaId) {
        composeMediaInteractions.removeTwMedia(mediaId);
      }
    });

    const unsubscribeURLUploadToCompose = subscribe(EVENTS.UPLOAD_URL_TO_COMPOSE, (urlList: string[]) => {
      if (urlList) {
        uploadUrlsToCompose(urlList.map((url) => ({ url, name: 'Shared media' })));
      }
    });

    const unsubscribeOpenUrl = subscribe(EVENTS.OPEN_ATTACH_FROM_URL_MODAL, (data?: { url?: string }) => {
      if (data?.url) {
        setUrlModalInitialUrl(data.url);
      } else {
        setUrlModalInitialUrl('');
      }
      setIsURLModalOpen(true);
    });

    return () => {
      unsubscribeReady();
      unsubscribeFailed();
      unsubscribeURLUploadToCompose();
      unsubscribeOpenUrl();
    };
  }, []);

  return (
    <ComposeBoxContext.Provider
      value={{
        activePostType,
        activeComposeBoxTab,
        profileListServices,
        activeNetworkTabs,
        isCanvaModalOpen,
        isComposeBoxOpen,
        isFlickrModalOpen,
        isGiphyModalOpen,
        isMediaAttacherOpen,
        isPexelsModalOpen,
        isRecentlyUsedModalOpen,
        isURLModalOpen,
        isYoutubeModalOpen,
        urlModalInitialUrl,
        isGoogleDriveModalOpen,
        setIsCanvaModalOpen,
        setIsFlickrModalOpen,
        setIsGiphyModalOpen,
        setIsPexelsModalOpen,
        setIsRecentlyUsedModalOpen,
        setIsURLModalOpen,
        setIsYoutubeModalOpen,
        setUrlModalInitialUrl,
        setIsGoogleDriveModalOpen,
        uploadFromDevice,
        uploadUrlsToCompose,
        attachExistingMedia,
        getRemainingMediaLimit,
      }}
    >
      {children}
    </ComposeBoxContext.Provider>
  );
};

export const useComposeBoxContext = (): ComposeBoxContextType => useContext(ComposeBoxContext);
